import React, { useState, useEffect } from 'react';
import { CheckSquare, Clock, Users, FileText, Calendar, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api, { FILE_BASE_URL } from '../services/api';

const MyTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchMyTasks();
  }, [filterDate]);

  const fetchMyTasks = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterDate) {
        params.startDate = new Date(filterDate).setHours(0, 0, 0, 0);
        params.endDate = new Date(filterDate).setHours(23, 59, 59, 999);
      }
      const data = await api.getMyTasks(params);
      const taskArray = Array.isArray(data) ? data : (data?.data || []);
      setTasks(taskArray);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching my tasks:', error);
      setTasks([]);
      setLoading(false);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await api.updateTaskStatus(taskId, newStatus);
      // Fetch updated tasks to reflect changes
      fetchMyTasks();
    } catch (error) {
      alert('Error updating status: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-tg-orange-200 border-t-tg-orange-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-bold uppercase tracking-widest text-xs">Loading Your Tasks...</p>
        </div>
      </div>
    );
  }

  // Filter tasks into Active and Completed
  const activeTasks = tasks.filter(t => t.status !== 'Completed');
  const completedTasks = tasks.filter(t => t.status === 'Completed');

  return (
    <div className="space-y-10 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-2"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-tg-orange-500 rounded-2xl shadow-lg shadow-tg-orange-500/20">
              <CheckSquare size={24} className="text-white" />
            </div>
            <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight uppercase">
              My <span className="text-tg-orange-500">Tasks</span>
            </h1>
          </div>
          <p className="text-slate-500 dark:text-slate-400 font-bold tracking-wide uppercase text-[10px] pl-1">
            Manage your assigned operations and deliverables
          </p>
        </motion.div>

        {/* Stats Summary */}
        <div className="flex gap-4">
          <div className="glass-card px-6 py-4 flex flex-col items-center justify-center border-slate-200 dark:border-slate-800">
            <span className="text-[8px] font-black uppercase tracking-[0.2em] text-slate-400">Total Missions</span>
            <span className="text-2xl font-black text-slate-900 dark:text-white">{tasks.length}</span>
          </div>
          <div className="glass-card px-6 py-4 flex flex-col items-center justify-center border-tg-orange-500/20">
            <span className="text-[8px] font-black uppercase tracking-[0.2em] text-tg-orange-500">Active</span>
            <span className="text-2xl font-black text-tg-orange-500">{activeTasks.length}</span>
          </div>
          <div className="glass-card px-6 py-4 flex flex-col items-center justify-center border-emerald-500/20">
            <span className="text-[8px] font-black uppercase tracking-[0.2em] text-emerald-500">Completed</span>
            <span className="text-2xl font-black text-emerald-500">{completedTasks.length}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Active Tasks Section */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 pb-3">
            <AlertCircle size={20} className="text-amber-500" />
            <h2 className="text-lg font-black uppercase tracking-widest text-slate-800 dark:text-white">Active Operations ({activeTasks.length})</h2>
          </div>

          <div className="space-y-4">
            <AnimatePresence>
              {activeTasks.length > 0 ? activeTasks.map((task, idx) => (
                <TaskCard 
                  key={task._id} 
                  task={task} 
                  idx={idx} 
                  onStatusChange={handleStatusChange} 
                />
              )) : (
                <div className="p-10 text-center glass-card border-dashed">
                  <CheckSquare size={32} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 font-bold uppercase tracking-widest text-[10px]">No active tasks assigned to you.</p>
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Completed Tasks Section */}
        <div className="space-y-6">
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
            <div className="flex items-center gap-3">
              <CheckSquare size={20} className="text-emerald-500" />
              <h2 className="text-lg font-black uppercase tracking-widest text-slate-800 dark:text-white">Completed Operations ({completedTasks.length})</h2>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-[8px] font-black uppercase text-slate-400">History Filter:</span>
              <input 
                type="date" 
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="bg-slate-100 dark:bg-slate-800 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 outline-none focus:border-tg-orange-500 transition-colors"
              />
              {filterDate !== new Date().toISOString().split('T')[0] && (
                <button 
                  onClick={() => setFilterDate(new Date().toISOString().split('T')[0])}
                  className="text-[10px] font-black uppercase text-tg-orange-500 hover:text-tg-orange-600 transition-colors"
                >
                  Back to Today
                </button>
              )}
            </div>
          </div>

          <div className="space-y-4 opacity-70">
            <AnimatePresence>
              {completedTasks.length > 0 ? completedTasks.slice(0, 10).map((task, idx) => (
                <TaskCard 
                  key={task._id} 
                  task={task} 
                  idx={idx} 
                  onStatusChange={handleStatusChange} 
                  isCompleted={true}
                />
              )) : (
                <div className="p-10 text-center glass-card border-dashed">
                  <CheckSquare size={32} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 font-bold uppercase tracking-widest text-[10px]">No completed tasks yet.</p>
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

const TaskCard = ({ task, idx, onStatusChange, isCompleted }) => {
  const isUrgent = task.priority === 'Urgent' || task.priority === 'High';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ delay: idx * 0.05 }}
      className={`glass-card p-6 flex flex-col gap-4 relative overflow-hidden group ${
        isCompleted ? 'border-emerald-500/30' : isUrgent ? 'border-red-500/30' : 'border-slate-200 dark:border-slate-800'
      }`}
    >
      {isUrgent && !isCompleted && (
        <div className="absolute top-0 right-0 bg-red-500 text-white text-[8px] font-black uppercase tracking-widest px-3 py-1 rounded-bl-xl shadow-lg z-20">
          {task.priority}
        </div>
      )}

      <div className="flex justify-between items-start">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight leading-tight">{task.title}</h3>
            <span className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-[0.1em] ${
              task.priority === 'Urgent' ? 'bg-red-100 text-red-600' :
              task.priority === 'High' ? 'bg-orange-100 text-orange-600' :
              task.priority === 'Medium' ? 'bg-blue-100 text-blue-600' :
              'bg-slate-100 text-slate-600'
            }`}>
              {task.priority}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Users size={12} className="text-slate-400" />
              <span className="text-[10px] font-black text-slate-600 dark:text-slate-400 uppercase tracking-widest">
                {task.client?.name || 'External Client'}
              </span>
            </div>
            {task.package?.name && (
              <div className="flex items-center gap-2">
                <FileText size={12} className="text-slate-400" />
                <span className="text-[10px] font-bold text-tg-orange-500 uppercase tracking-widest">
                  {task.package.name}
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Status Dropdown */}
        <div className="shrink-0 z-10">
          <select
            value={task.status}
            onChange={(e) => onStatusChange(task._id, e.target.value)}
            className={`text-[10px] font-black uppercase tracking-widest px-4 py-2.5 rounded-xl outline-none appearance-none cursor-pointer border shadow-sm transition-all hover:scale-105 ${
              task.status === 'Completed' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
              task.status === 'In Progress' ? 'bg-blue-50 text-blue-600 border-blue-200' :
              task.status === 'Review' ? 'bg-purple-50 text-purple-600 border-purple-200' :
              'bg-amber-50 text-amber-600 border-amber-200'
            }`}
          >
            <option value="Pending">Pending</option>
            <option value="In Progress">In Progress</option>
            <option value="Review">Review</option>
            <option value="Completed">Completed</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-1">
        <div className="bg-slate-50 dark:bg-slate-800/50 p-2.5 rounded-xl flex items-center gap-2.5 border border-slate-100 dark:border-slate-800">
          <Calendar size={13} className="text-tg-orange-500" />
          <div>
            <p className="text-[7px] font-black text-slate-400 uppercase tracking-widest">Assign Time</p>
            <p className="text-[10px] font-bold text-slate-700 dark:text-slate-300">
              {new Date(task.assignedDate || task.createdAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
            </p>
          </div>
        </div>
        
        <div className="bg-slate-50 dark:bg-slate-800/50 p-2.5 rounded-xl flex items-center gap-2.5 border border-slate-100 dark:border-slate-800">
          <Clock size={13} className="text-tg-orange-500" />
          <div>
            <p className="text-[7px] font-black text-slate-400 uppercase tracking-widest">Due Time</p>
            <p className="text-[10px] font-bold text-slate-700 dark:text-slate-300">{task.dueTime || '09:00'}</p>
          </div>
        </div>

        {isCompleted && (
          <div className="bg-emerald-50 dark:bg-emerald-500/10 p-2.5 rounded-xl flex items-center gap-2.5 border border-emerald-100 dark:border-emerald-500/20">
            <CheckSquare size={13} className="text-emerald-500" />
            <div>
              <p className="text-[7px] font-black text-emerald-500 uppercase tracking-widest">Complete Time</p>
              <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                {new Date(task.completedDate || task.updatedAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
              </p>
            </div>
          </div>
        )}
        
        <div className="bg-slate-50 dark:bg-slate-800/50 p-2.5 rounded-xl flex items-center gap-2.5 border border-slate-100 dark:border-slate-800">
          <AlertCircle size={13} className="text-tg-orange-500" />
          <div>
            <p className="text-[7px] font-black text-slate-400 uppercase tracking-widest">Type</p>
            <p className="text-[10px] font-bold text-slate-700 dark:text-slate-300 uppercase">{task.type || 'Other'}</p>
          </div>
        </div>
      </div>

      {task.instructions && (
        <div className="mt-1 text-xs text-slate-600 dark:text-slate-400 bg-slate-50/80 dark:bg-slate-900 p-4 rounded-xl border border-slate-100 dark:border-slate-800">
          <p className="font-black mb-2 text-slate-800 dark:text-slate-300 text-[9px] uppercase tracking-widest flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-tg-orange-500 rounded-full" />
            Strategic Instructions:
          </p>
          <p className="leading-relaxed font-medium italic text-[11px]">"{task.instructions}"</p>
        </div>
      )}

      {/* Deliverables & Milestones */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {task.impactedDeliverables?.length > 0 && (
          <div className="space-y-2">
            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Target Deliverables:</p>
            <div className="flex flex-wrap gap-2">
              {task.impactedDeliverables.map((item, i) => (
                <div key={i} className="px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-500/20 text-[9px] font-black uppercase tracking-widest">
                  {item.name} ({item.count})
                </div>
              ))}
            </div>
          </div>
        )}

        {task.milestones?.length > 0 && (
          <div className="space-y-2">
            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Phase Milestones:</p>
            <div className="space-y-1.5">
              {task.milestones.map((m, i) => (
                <div key={i} className="flex items-center gap-2 text-[10px] font-bold text-slate-600 dark:text-slate-400">
                  <div className={`w-3 h-3 rounded-md border flex items-center justify-center ${m.completed ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 dark:border-slate-700'}`}>
                    {m.completed && <CheckSquare size={8} className="text-white" />}
                  </div>
                  <span className={m.completed ? 'line-through opacity-50' : ''}>{m.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {task.referenceFiles?.length > 0 && (
        <div className="space-y-3 mt-1">
          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Asset Resources & Previews:</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {task.referenceFiles.map((file, i) => {
              const isImage = file.mimetype?.startsWith('image/');
              const fileUrl = file.url.startsWith('http') ? file.url : `${FILE_BASE_URL}${file.url.startsWith('/') ? '' : '/'}${file.url}`;
              
              return (
                <div key={i} className="group/file relative">
                  {isImage ? (
                    <div className="aspect-square rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800">
                      <img src={fileUrl} alt={file.originalName} className="w-full h-full object-cover transition-transform group-hover/file:scale-110" />
                      <a 
                        href={fileUrl} 
                        target="_blank" 
                        rel="noreferrer"
                        className="absolute inset-0 bg-black/40 opacity-0 group-hover/file:opacity-100 flex items-center justify-center transition-opacity"
                      >
                        <FileText size={20} className="text-white" />
                      </a>
                    </div>
                  ) : (
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex flex-col items-center justify-center aspect-square rounded-xl bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-tg-orange-500/50 transition-all shadow-sm"
                    >
                      <FileText size={24} className="text-slate-400 mb-2" />
                      <span className="text-[8px] font-black uppercase tracking-widest px-2 text-center truncate w-full">{file.originalName}</span>
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default MyTasks;
